import type { Job } from "../../../../../../../models";
import type {
	WizardIssues,
	WizardStep,
	OverlapIssue,
	DuplicateIssue,
	InternalDuplicateIssue,
	OverlappingZonesIssue,
	UnmatchedIssue,
	MissingFeaturesIssue,
	InvalidDataIssue,
} from "../../WizardContainer/WizardContainer.types";
import { WIZARD_STEP_TITLES, VALIDATION_STEPS } from "../../WizardContainer/WizardContainer.types";
import type { AffectedJobInfo, IssueListItem, IssueGroup } from "./IssueSummaryPopover.types";

// ============================================================================
// Constants
// ============================================================================

/** Maps each WizardIssues key to its corresponding WizardStep */
const ISSUE_KEY_TO_STEP: Record<keyof WizardIssues, WizardStep> = {
	unmatched: "unmatched-features",
	duplicateBoundaries: "duplicate-boundaries",
	missingFeatures: "missing-features",
	invalidData: "missing-invalid-data",
	overlaps: "overlapping-boundaries",
	overlappingZones: "overlapping-zones",
	overlappingPoints: "overlapping-points",
};

// ============================================================================
// Helpers
// ============================================================================

function extractJobInfo(job: Job): AffectedJobInfo {
	return {
		grower: job.growerName || job.importGrowerName || "",
		farm: job.farmName || job.importFarmName || "",
		field: job.fieldName || job.importFieldName || "",
	};
}

function extractJobs(
	issue:
		| OverlapIssue
		| DuplicateIssue
		| InternalDuplicateIssue
		| OverlappingZonesIssue
		| UnmatchedIssue
		| MissingFeaturesIssue
		| InvalidDataIssue,
): AffectedJobInfo[] {
	if ("jobs" in issue && Array.isArray(issue.jobs)) {
		return issue.jobs.map(extractJobInfo);
	}
	if ("job" in issue && issue.job) {
		return [extractJobInfo(issue.job as Job)];
	}
	return [];
}

function describeIssue(
	key: keyof WizardIssues,
	issue:
		| OverlapIssue
		| DuplicateIssue
		| InternalDuplicateIssue
		| OverlappingZonesIssue
		| UnmatchedIssue
		| MissingFeaturesIssue
		| InvalidDataIssue,
): string {
	switch (key) {
		case "overlaps":
			return `Boundaries overlap (${(issue as OverlapIssue).intersectionArea} sq m)`;
		case "duplicateBoundaries":
			return `${(issue as DuplicateIssue).duplicateCount} duplicate boundaries`;
		case "overlappingPoints":
			return `${(issue as InternalDuplicateIssue).duplicateGroups.length} groups of duplicate points`;
		case "overlappingZones":
			return `${(issue as OverlappingZonesIssue).overlapGroups.length} groups of overlapping zones`;
		case "unmatched":
			return `${(issue as UnmatchedIssue).unmatchedFeatures.features.length} unmatched features`;
		case "missingFeatures":
			return "Missing points/zones";
		case "invalidData": {
			const fields = Object.keys((issue as InvalidDataIssue).errors);
			return `Missing: ${fields.join(", ")}`;
		}
	}
}

// ============================================================================
// Exported Functions
// ============================================================================

/**
 * Builds a flat list of all issues across all steps for display in the popover.
 */
export function buildAllIssueList(issues: WizardIssues, skippedIssueIds: Set<string>): IssueListItem[] {
	const result: IssueListItem[] = [];

	for (const [key, step] of Object.entries(ISSUE_KEY_TO_STEP) as [keyof WizardIssues, WizardStep][]) {
		const issueArray = issues[key];
		for (let i = 0; i < issueArray.length; i++) {
			const issue = issueArray[i];
			result.push({
				issueId: issue.id,
				step,
				stepTitle: WIZARD_STEP_TITLES[step],
				issueIndex: i,
				description: describeIssue(key, issue),
				jobs: extractJobs(issue),
				isSkipped: skippedIssueIds.has(issue.id),
			});
		}
	}

	return result;
}

/**
 * Groups a flat issue list by step, preserving VALIDATION_STEPS order.
 * Returns only non-empty groups.
 */
export function groupIssuesByStep(items: IssueListItem[]): IssueGroup[] {
	if (items.length === 0) return [];

	const byStep = new Map<WizardStep, IssueListItem[]>();
	for (const item of items) {
		const existing = byStep.get(item.step);
		if (existing) {
			existing.push(item);
		} else {
			byStep.set(item.step, [item]);
		}
	}

	const groups: IssueGroup[] = [];
	for (const step of VALIDATION_STEPS) {
		const stepItems = byStep.get(step);
		if (stepItems && stepItems.length > 0) {
			groups.push({
				step,
				stepTitle: WIZARD_STEP_TITLES[step],
				items: stepItems,
			});
		}
	}

	return groups;
}
